import React from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { LinearGradient } from 'expo-linear-gradient';
import Spacer from '@/Atoms/Spacer';

import CCButton from '@/Atoms/CCButton';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';

interface OpeningPerformanceReportProps {
  onBackPress?: () => void;
  userName?: string;
  userPoints?: number;
  userLevel?: string;
  streakDays?: number;
  pointsToNextLevel?: number;
  openingScore?: number;
  bestGameScore?: number;
  lowestGameScore?: number;
  reportDate?: string;
  gamesAnalyzed?: number;
  gameScores?: number[];
  onRunAssessment?: () => void;
  onSeeLeaderboard?: () => void;
  onOpenModelGame?: (index: number) => void;
  onPracticeKeyMoments?: (index: number) => void;
  onSolvePatternDrills?: () => void;
  onTrainPunishments?: () => void;
  onSeeAllGoodMoves?: () => void;
  onPracticeStrength?: () => void;
  onSeeViolations?: () => void;
  onPracticePrinciples?: () => void;
  selectedOpening?: string;
  onOpeningChange?: (opening: string) => void;
}


const OpeningPerformanceReport: React.FC<OpeningPerformanceReportProps> = ({
  onBackPress,
  userName = 'Sumit Mohan',
  userPoints = 1250,
  userLevel = 'Beginner',
  streakDays = 2,
  pointsToNextLevel = 10,
  openingScore = 75,
  bestGameScore = 89,
  lowestGameScore = 61,
  reportDate = 'May 18, 2025',
  gamesAnalyzed = 6,
  gameScores = [89, 89, 89, 89, 89, 89],
  onRunAssessment,
  onSeeLeaderboard,
  onOpenModelGame,
  onPracticeKeyMoments,
  onSolvePatternDrills,
  onTrainPunishments,
  onSeeAllGoodMoves,
  onPracticeStrength,
  onSeeViolations,
  onPracticePrinciples,
  selectedOpening = 'London system-1',
  onOpeningChange,
}) => {
  // Strengths data
  const strengths = [
    { title: 'Full control power', games: '5 / 6 games' },
    { title: 'Initial content is easily develop', games: '5 / 6 games' },
    { title: 'Experts Up 3 Crosses (Run)', games: '5 / 6 games' },
    { title: 'Strong play in Teacher structures', games: '5 / 6 games' },
  ];

  // Improvements data with correct colors from Figma
  const improvements = [
    { title: 'Development', score: 75, color: '#E17846' },
    { title: 'Center Control', score: 75, color: '#937ADB' },
    { title: 'Pawn Coordination', score: 75, color: '#DB7ACC' },
    { title: 'Piece Equality', score: 75, color: '#67B3E1' },
    { title: 'King Safety', score: 75, color: '#3DAB9E' },
  ];

  // Recurring errors data
  const recurringErrors = [
    { title: 'Same piece moved twice early', count: '2 games' },
    { title: 'Early pawn pushes without reason', count: '2 games' },
    { title: 'Delayed castling', count: '2 games' },
  ];

  // Opponent errors data
  const opponentErrors = [
    { title: 'Same piece moved twice early', count: '2 games' },
    { title: 'Early pawn pushes without reason', count: '2 games' },
  ];

  // Critical positions data
  const criticalPositions = [
    { title: 'Early knight sortie', count: '3 occurrences' },
    { title: 'Passive bishop placement', count: '3 occurrences' },
    { title: 'Missed central break timing', count: '3 occurrences' },
  ];

  // Model games data
  const modelGames = [
    { title: 'Classic model game', reason: 'Better development structure' },
    { title: 'Positional punishment example', reason: 'Convert opponent inaccuracies' },
    { title: 'Modern GM example (similar structure)', reason: 'Better development structure' },
  ];

  // Performance summary cards
  const performanceCards = [
    {
      title: 'Why Your Score is 75/100',
      content:
        'Your opening score of 75/100 reflects a solid foundation with room for improvement. You excel at piece development and maintaining central control, which is evident in most of your games. However, inconsistencies in pawn structure discipline and premature piece movements are holding you back from reaching the 80+ range. With more precise opening play, you can enter the middlegame with stronger positions.',
      color: CCColors.Green,
      bgColor: 'rgba(61, 171, 158, 0.16)',
      borderColor: CCColors.Green,
    },
    {
      title: 'How You Compare to Elite Players (2500+ GM/IM)',
      content:
        "Grandmasters rated 2500+ typically score 90-95+/100 in opening performance. Your score of 75/100 shows you're 11-21 points away from elite-level opening play. The gap is primarily in principle consistency and precision timing—areas where top players rarely deviate from optimal plans.",
      color: CCColors.Pink,
      bgColor: 'rgba(219, 122, 204, 0.16)',
      borderColor: CCColors.Pink,
    },
  ];

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CCButton
              hoverEffect={false}
              onPress={onBackPress}
              type="none"
              style={styles.backButton}
              contentContainerStyle={{ paddingHorizontal: 0, paddingVertical: 0 }}>
              <Box flexDirection='row' alignItems='center' gap={10}>
                <Ionicons name="arrow-back" size={20} color={CCColors.TextColor.Dark} />
                <CCText style={styles.backText}>Back</CCText>
              </Box>
            </CCButton>
          </View>
          <View style={styles.headerRight}>
            <View style={styles.openingSelector}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Primary}>
                {selectedOpening}
              </CCText>
              <Feather name="chevron-down" size={16} color={CCColors.TextColor.Primary} />
            </View>
          </View>
        </View>

        {/* Title Section */}
        <View style={styles.titleSection}>
          <View style={styles.titleRow}>
            <CCText style={styles.mainTitle}>Opening Performance Report</CCText>
            <CCButton
              style={styles.runAssessmentButton}
              onPress={onRunAssessment}
              type="fill"
              contentContainerStyle={{ paddingHorizontal: 24, paddingVertical: 12 }}>
              <CCText style={styles.runAssessmentText}>Run assessment</CCText>
            </CCButton>
          </View>
          <CCText style={styles.subtitle}>
            Last {gamesAnalyzed} games analyzed • Report date: {reportDate}
          </CCText>
        </View>

        {/* Overall Opening Score Card */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <CCText style={styles.cardTitle}>Overall Opening Score</CCText>
            {/* <TouchableOpacity style={styles.seeLeaderboardButton} onPress={onSeeLeaderboard}>
              <CCText style={styles.seeLeaderboardText}>See Leaderboard</CCText>
            </TouchableOpacity> */}
          </View>

          <View style={styles.scoreStatsRow}>
            {/* Main Score with gradient */}
            <View style={styles.mainScoreCard}>
              <LinearGradient
                colors={['rgba(250, 207, 71, 0.4)', 'rgba(250, 207, 71, 0.6)']}
                style={styles.gradientScoreBg}
              />
              <View style={styles.scoreContent}>
                <CCText style={styles.mainScore}>{openingScore}</CCText>
                <CCText style={styles.scoreLabel}>Out of 100</CCText>
              </View>
            </View>

            {/* Best Game Score */}
            <View style={styles.statCard}>
              <CCText style={styles.statScore}>{bestGameScore}</CCText>
              <CCText style={styles.statLabel}>Best game score</CCText>
            </View>

            {/* Lowest Game Score */}
            <View style={styles.statCard}>
              <CCText style={styles.statScore}>{lowestGameScore}</CCText>
              <CCText style={styles.statLabel}>Lowest game score</CCText>
            </View>
          </View>
        </View>

        {/* Game-by-Game Performance */}
        <View style={styles.card}>
          <CCText style={styles.cardTitle}>Game-by-Game Performance</CCText>
          <View style={styles.gameScoresRow}>
            {gameScores.map((score, index) => (
              <View key={index} style={styles.gameScoreCard}>
                <CCText style={styles.gameScoreValue}>{score}</CCText>
                <CCText style={styles.gameScoreLabel}>Game {index + 1}</CCText>
              </View>
            ))}
          </View>
        </View>

        {/* Performance Summary */}
        <View style={styles.card}>
          <CCText style={styles.cardTitle}>Performance Summary</CCText>
          {performanceCards.map((card, index) => (
            <View
              key={index}
              style={[
                styles.performanceCard,
                { backgroundColor: card.bgColor, borderColor: card.borderColor },
              ]}>
              <CCText style={styles.performanceCardTitle}>{card.title}</CCText>
              <CCText style={styles.performanceCardContent}>{card.content}</CCText>
            </View>
          ))}
        </View>

        {/* Strengths and Improvements */}
        <View style={styles.twoColumnContainer}>
          {/* Your Opening Strengths */}
          {/* <View style={styles.columnCard}>
            <CCText style={styles.cardTitle}>Your Opening Strengths</CCText>
            <View style={[styles.bestMomentCard, { borderColor: CCColors.Green }]}>
              <CCText style={styles.bestMomentTitle}>Best Repeated Moment</CCText>
              <CCText style={styles.bestMomentQuote}>
                "You consistently choose natural developing moves."
              </CCText>
            </View>
            {strengths.map((strength, index) => (
              <View key={index} style={styles.strengthItem}>
                <View style={[styles.checkIconBg, { backgroundColor: CCColors.Green }]}>
                  <Feather name="check" size={12} color={CCColors.White} />
                </View>
                <View style={styles.strengthContent}>
                  <CCText style={styles.strengthTitle}>{strength.title}</CCText>
                  <CCText style={styles.strengthGames}>{strength.games}</CCText>
                </View>
              </View>
            ))}
            <View style={styles.buttonRow}>
              <CCButton
                style={styles.actionButton}
                onPress={onSeeAllGoodMoves}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}>
                <CCText style={styles.actionButtonText}>See all good moves</CCText>
              </CCButton>
              <CCButton
                style={styles.actionButton}
                onPress={onPracticeStrength}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}>
                <CCText style={styles.actionButtonText}>Practice Strength</CCText>
              </CCButton>
            </View>
          </View> */}

          {/* Overall Score with Progress Bars */}
          <View style={styles.columnCard}>
            <CCText style={styles.smallCardTitle}>Overall Score</CCText>
            <CCText style={styles.overallScoreText}>{openingScore} / 100</CCText>

            {/* Progress Bars */}
            {improvements.map((item, index) => (
              <Box flexDirection='row' gap={20} alignItems='center'>
                <View key={index} style={styles.progressItem}>
                  <View style={styles.progressHeader}>
                    <CCText style={styles.progressTitle}>{item.title}</CCText>
                    <CCText style={[styles.progressScore, { color: item.color }]}>
                      {item.score}
                    </CCText>
                  </View>
                  <View style={styles.progressBarBg}>
                    <View
                      style={[
                        styles.progressBarFill,
                        { width: `${item.score}%`, backgroundColor: item.color },
                      ]}
                    />
                  </View>
                </View>
                <CCButton type="fill" titleStyle={{ fontSize: 12, color: CCColors.Black }}>
                  Practice Principles
                </CCButton>
              </Box>
            ))}

            <Box flexDirection='row' gap={20} style={[styles.mostBrokenCard, { borderColor: CCColors.Red }]}>
              <Ionicons name="alert-circle-outline" size={30} color={CCColors.Red} />
              <View>
                <View style={styles.mostBrokenHeader}>
                  <CCText style={styles.mostBrokenTitle}>Most Broken Principle</CCText>
                </View>
                <CCText style={styles.mostBrokenQuote}>"Too many early pawn moves"</CCText>
              </View>
            </Box>

            <View style={styles.buttonRow}>
              <CCButton
                style={styles.actionButton}
                onPress={onSeeViolations}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}>
                <CCText style={styles.actionButtonText}>See violations</CCText>
              </CCButton>
            </View>
          </View>
        </View>

        {/* Recurring Opening Errors */}
        <View style={[styles.card, { backgroundColor: 'transparent', shadowOffset: { width: 0, height: 0 }, }]}>
          <CCText style={styles.cardTitle}>Recurring Opening Errors</CCText>
          <View style={styles.threeColumnContainer}>
            {/* Your Errors */}
            <View style={styles.errorColumn}>
              <View>
                <CCText style={styles.columnTitle}>Your errors</CCText>
                {recurringErrors.map((error, index) => (
                  <View key={index} style={styles.errorItem}>
                    <CCText style={styles.errorTitle}>{error.title}</CCText>
                    <CCText style={styles.errorCount}>{error.count}</CCText>
                  </View>
                ))}
              </View>
              <CCButton
                style={styles.actionButtonFull}
                onPress={onSolvePatternDrills}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <CCText style={styles.actionButtonText}>Solve pattern drills</CCText>
              </CCButton>
            </View>

            {/* Opponent Errors */}
            <View style={styles.errorColumn}>
              <View>
                <CCText style={styles.columnTitle}>Opponent Errors</CCText>
                {opponentErrors.map((error, index) => (
                  <View key={index} style={styles.errorItem}>
                    <CCText style={styles.errorTitle}>{error.title}</CCText>
                    <CCText style={styles.errorCount}>{error.count}</CCText>
                  </View>
                ))}
              </View>
              <CCButton
                style={styles.actionButtonFull}
                onPress={onTrainPunishments}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <CCText style={styles.actionButtonText}>Train punishments</CCText>
              </CCButton>
            </View>

            {/* Critical Positions */}
            {/* <View style={styles.errorColumn}>
              <CCText style={styles.columnTitle}>Critical Recurring Positions</CCText>
              {criticalPositions.map((position, index) => (
                <View key={index} style={styles.errorItem}>
                  <CCText style={styles.errorTitle}>{position.title}</CCText>
                  <CCText style={styles.errorCount}>{position.count}</CCText>
                </View>
              ))}
              <CCButton
                style={styles.actionButtonFull}
                onPress={onSolvePatternDrills}
                type="fill"
                contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <CCText style={styles.actionButtonText}>Solve pattern drills</CCText>
              </CCButton>
            </View> */}
          </View>
        </View>


        {/* Model Games to Study */}
        <View style={styles.card}>
          <CCText style={styles.cardTitle}>Model Games to Study</CCText>
          <Spacer spacing={20} />
          {modelGames.map((game, index) => (
            <View key={index} style={styles.modelGameCard}>
              <View style={styles.modelGameContent}>
                <CCText style={styles.modelGameTitle}>{game.title}</CCText>
                <CCText style={styles.modelGameReason}>Reason: {game.reason}</CCText>
              </View>
              <View>
                <View style={styles.modelGameButtons}>
                  <CCButton
                    onPress={() => onOpenModelGame?.(index)}
                    type="outline"
                    style={styles.outlineButton}
                    contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}>
                    <CCText
                      style={[
                        styles.outlineButtonText,
                        { color: CCColors.TextColor.Dark },
                      ]}>
                      Open
                    </CCText>
                  </CCButton>
                  <CCButton
                    onPress={() => onPracticeKeyMoments?.(index)}
                    type="fill"
                    style={styles.yellowButton}
                    contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}>
                    <CCText style={styles.yellowButtonText}>
                      Practice key moments
                    </CCText>
                  </CCButton>
                </View>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CCColors.Primary.Light,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  backText: {
    fontSize: FontSizes[16],
    color: CCColors.TextColor.Dark,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  openingSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.White,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: CCColors.Border,
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
  },
  titleSection: {
    marginBottom: 16,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mainTitle: {
    fontSize: FontSizes[24],
    fontWeight: '700',
    color: CCColors.TextColor.Dark,
  },
  runAssessmentButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 32,
  },
  runAssessmentText: {
    fontSize: FontSizes[20],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  subtitle: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
    marginTop: 4,
  },
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: FontSizes[20],
    fontWeight: '500',
    color: CCColors.TextColor.Primary,
  },
  smallCardTitle: {
    fontSize: FontSizes[16],
    fontWeight: '500',
    color: 'rgba(77, 63, 55, 0.8)',
    marginBottom: 4,
  },
  seeLeaderboardButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 32,
  },
  seeLeaderboardText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  scoreStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    height: 150
  },
  mainScoreCard: {
    width: '42%',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  gradientScoreBg: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scoreContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainScore: {
    fontSize: 40,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  scoreLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
  },
  statCard: {
    width: '26%',
    aspectRatio: 1,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
  },
  statScore: {
    fontSize: 40,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  statLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
    textAlign: 'center',
  },
  gameScoresRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  gameScoreCard: {
    width: '14%',
    aspectRatio: 0.8,
    borderRadius: 8,
    backgroundColor: CCColors.Primary.Light,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gameScoreValue: {
    fontSize: 30,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
  },
  gameScoreLabel: {
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Primary,
  },
  performanceCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  performanceCardTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 8,
  },
  performanceCardContent: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
    lineHeight: 20,
  },
  twoColumnContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  columnCard: {
    flexGrow: 1,
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  bestMomentCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    backgroundColor: 'rgba(61, 171, 158, 0.08)',
  },
  bestMomentTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  bestMomentQuote: {
    fontSize: FontSizes[14],
    fontStyle: 'italic',
    color: 'rgba(77, 63, 55, 0.8)',
  },
  strengthItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkIconBg: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  smallCheckIcon: {
    width: 16,
    height: 16,
    tintColor: CCColors.White,
  },
  strengthContent: {
    flex: 1,
  },
  strengthTitle: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
  },
  strengthGames: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  actionButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 32,
    flex: 1,
    marginHorizontal: 4,
    alignItems: 'center',
  },
  actionButtonFull: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 32,
    alignItems: 'center',
    marginTop: 12,
  },
  actionButtonText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  overallScoreText: {
    fontSize: 24,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    marginBottom: 16,
  },
  mostBrokenCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    backgroundColor: 'rgba(225, 120, 70, 0.08)',
  },
  mostBrokenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  alertIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  mostBrokenTitle: {
    fontSize: FontSizes[16],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  mostBrokenQuote: {
    fontSize: FontSizes[14],
    color: CCColors.Red,
  },
  progressItem: {
    marginBottom: 12,
    flex: 1,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  progressTitle: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Primary,
  },
  progressScore: {
    fontSize: FontSizes[14],
    fontWeight: '700',
  },
  progressBarBg: {
    height: 16,
    backgroundColor: '#F6F6F6',
    borderRadius: 30,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#D9D9D9',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 30,
  },
  threeColumnContainer: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
  },
  errorColumn: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    justifyContent: 'space-between',
  },
  columnTitle: {
    fontSize: FontSizes[16],
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    marginBottom: 12,
  },
  errorItem: {
    marginBottom: 12,
  },
  errorTitle: {
    fontSize: FontSizes[13],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 2,
  },
  errorCount: {
    fontSize: FontSizes[14],
    color: 'rgba(77, 63, 55, 0.8)',
  },
  modelGameCard: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.2)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  modelGameContent: {
    marginBottom: 12,
  },
  modelGameTitle: {
    fontSize: FontSizes[14],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
  },
  modelGameReason: {
    fontSize: FontSizes[14],
    color: '#A8A7A6',
  },
  modelGameButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  outlineButton: {
    backgroundColor: CCColors.Primary.Light,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 32,
  },
  outlineButtonText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
  yellowButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 32,
  },
  yellowButtonText: {
    fontSize: FontSizes[14],
    fontWeight: '500',
    color: CCColors.TextColor.Dark,
  },
});

export default OpeningPerformanceReport;